import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaMicrophone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaEye,
  FaUsers,
  FaCheckCircle,
  FaHourglassHalf,
  FaEdit,
  FaTimes,
  FaSync,
  FaGlobe,
  FaBullseye,
  FaSpinner,
  FaTrash
} from "react-icons/fa";

const ApprovedPaidConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [allReporters, setAllReporters] = useState({});
  const [modifyLoading, setModifyLoading] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedReportersForResend, setSelectedReportersForResend] = useState([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const navigate = useNavigate();

  // Fetch approved conferences
  const fetchApprovedConferences = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/all?limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        const apiData = response.data.data; // Get the data property from response.data
        console.log("📊 Raw API data received:", apiData);

        // Check if apiData has the expected structure with conferences array
        if (apiData && apiData.conferences && Array.isArray(apiData.conferences)) {
          console.log(`📊 Total conferences received: ${apiData.conferences.length}`);

          // Debug: Show all statuses
          const allStatuses = apiData.conferences.map(conf => conf.status);
          console.log("📊 All conference statuses:", allStatuses);
          console.log("📊 Unique statuses:", [...new Set(allStatuses)]);

          // Show ALL conferences regardless of status - user wants to see all conferences
          const allConferences = apiData.conferences;

          console.log(`📊 All conferences (no status filtering): ${allConferences.length}`);
          console.log("📊 All conferences:", allConferences);

          setConferences(allConferences);
          console.log(`📊 State updated with ${allConferences.length} conferences`);

          // Fetch reporter data for all conferences to show correct counts in the main table
          allConferences.forEach(conference => {
            fetchConferenceReporters(conference.conferenceId, false);
          });
        } else if (Array.isArray(apiData)) {
          // Handle case where apiData is directly an array
          console.log(`📊 Direct array received: ${apiData.length} conferences`);

          // Debug: Show all statuses
          const allStatuses = apiData.map(conf => conf.status);
          console.log("📊 All conference statuses (direct array):", allStatuses);
          console.log("📊 Unique statuses (direct array):", [...new Set(allStatuses)]);

          // Show ALL conferences regardless of status - user wants to see all conferences
          const allConferences = apiData;

          console.log(`📊 All conferences (no status filtering, direct array): ${allConferences.length}`);
          setConferences(allConferences);
          console.log(`📊 State updated with ${allConferences.length} conferences (direct array)`);

          // Fetch reporter data for all conferences to show correct counts in the main table
          allConferences.forEach(conference => {
            fetchConferenceReporters(conference.conferenceId, false);
          });
        } else {
          console.error("API response.data.data is not in expected format:", apiData);
          console.log("Full response structure:", response.data);
          setConferences([]); // Fallback to empty array to prevent errors
          setError("Received invalid data format from server.");
        }
      } else {
        console.error("API returned success: false:", response.data);
        setConferences([]);
        setError("Failed to fetch conferences from server.");
      }
    } catch (error) {
      console.error("Error fetching approved conferences:", error);
      setError("Failed to load conferences");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchApprovedConferences(true); // Show loading on initial load
  }, []);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Optionally set to end of day if endOfDay=true
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return null;
    }
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const filteredConferences = conferences.filter((conference) => {
    // Status filtering is already done in fetchApprovedConferences, so we don't need to filter again
    // if (conference.status !== "approved" && conference.status !== "modified") return false;

    console.log(`🔍 Filtering conference ${conference.conferenceId} with status: ${conference.status}`);

    // Convert conference dates to Date objects
    const conferenceDate = toDate(conference.conferenceDate);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip conferences with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !conferenceDate) {
      return false;
    }

    if (todayOnly) {
      const today = toDate(new Date());
      if (!conferenceDate || conferenceDate.getTime() !== today.getTime()) {
        return false;
      }
    } else if (filterStart && filterEnd) {
      if (!conferenceDate || conferenceDate < filterStart || conferenceDate > filterEnd) {
        return false;
      }
    } else if (filterStart) {
      if (!conferenceDate || conferenceDate < filterStart) {
        return false;
      }
    } else if (filterEnd) {
      if (!conferenceDate || conferenceDate > filterEnd) {
        return false;
      }
    }

    // Search filter
    if (searchText) {
      const s = searchText.toLowerCase();
      const idMatch = conference.conferenceId && conference.conferenceId.toLowerCase().includes(s);
      const topicMatch = conference.topic && conference.topic.toLowerCase().includes(s);
      const stateMatch = conference.state && conference.state.toLowerCase().includes(s);
      const cityMatch = conference.city && conference.city.toLowerCase().includes(s);
      const submitterMatch = conference.submittedBy?.name && conference.submittedBy.name.toLowerCase().includes(s);

      if (!idMatch && !topicMatch && !stateMatch && !cityMatch && !submitterMatch) return false;
    }

    console.log(`✅ Conference ${conference.conferenceId} passed all filters`);
    return true;
  });

  console.log(`📊 Total conferences in state: ${conferences.length}`);
  console.log(`📊 Final filtered conferences count: ${filteredConferences.length}`);
  console.log(`📊 Filtered conferences:`, filteredConferences.map(c => ({ id: c.conferenceId, status: c.status })));



  // Fetch all reporters who have the conference in their panel (targeted reporters)
  const fetchConferenceReporters = async (conferenceId, forceRefresh = false) => {
    try {
      console.log(`🔍 Fetching reporters for PAID conference ${conferenceId}, forceRefresh: ${forceRefresh}`);

      // Add timestamp to prevent caching
      const timestamp = forceRefresh ? `?t=${Date.now()}` : '';

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/paid-conference/${conferenceId}/targeted-reporters${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 PAID Conference API Response:`, response.data);

      if (response.data.success) {
        console.log(`📊 Fetched reporters for PAID conference ${conferenceId}:`, response.data.data);
        setAllReporters(prev => ({
          ...prev,
          [conferenceId]: response.data.data
        }));
      } else {
        console.error(`❌ API returned success: false for PAID conference ${conferenceId}:`, response.data);
      }
    } catch (error) {
      console.error(`❌ Error fetching PAID conference targeted reporters for ${conferenceId}:`, error);
      if (error.response) {
        console.error(`❌ Error response:`, error.response.data);
        console.error(`❌ Error status:`, error.response.status);
      }
    }
  };

  // View conference details
  const handleViewDetails = async (conference) => {
    console.log(`🔍 Opening PAID conference details for: ${conference.conferenceId}`);
    setSelectedConference(conference);
    setShowModal(true);

    // Fetch complete conference details with adminTargeting information
    try {
      console.log(`🔍 Fetching complete PAID conference details for: ${conference.conferenceId}`);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/paid-conference/${conference.conferenceId}/targeted-reporters`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Complete PAID conference details response:`, response.data);

      if (response.data.success) {
        console.log("🔍 Fetched complete PAID conference details:", response.data.data);

        // Update the selected conference with complete details including adminTargeting
        setSelectedConference(prev => ({
          ...prev,
          ...response.data.data,
          adminTargeting: response.data.data.targetingInfo || null
        }));

        // Also update the allReporters state with the fetched data
        setAllReporters(prev => ({
          ...prev,
          [conference.conferenceId]: response.data.data
        }));
      } else {
        console.error("❌ Failed to fetch complete PAID conference details:", response.data);
      }
    } catch (error) {
      console.error("❌ Error fetching complete PAID conference details:", error);
      if (error.response) {
        console.error("❌ Error response:", error.response.data);
        console.error("❌ Error status:", error.response.status);
      }
    }
  };

  // Handle modify conference
  const handleModifyConference = (conference) => {
    navigate("/admin/press-conference/paid-conference/modify", {
      state: {
        conferenceId: conference.conferenceId,
        conferenceData: conference
      }
    });
  };

  // Handle resend to all reporters
  const handleResendToReporters = async (conference) => {
    setModifyLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conference.conferenceId}/action`,
        {
          action: "modified",
          note: "Resending to all reporters with updated targeting",
          // Keep existing targeting or use default
          allStates: conference.adminTargeting?.allStates || false,
          selectedStates: conference.adminTargeting?.adminSelectState || [],
          adminSelectCities: conference.adminTargeting?.adminSelectCities || [],
          adminSelectPincode: conference.adminTargeting?.adminSelectPincode || "",
          reporterId: conference.adminTargeting?.reporterId || []
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the conferences list
        await fetchApprovedConferences();

        // If modal is open, refresh the reporter data
        if (selectedConference && selectedConference.conferenceId === conference.conferenceId) {
          await fetchConferenceReporters(conference.conferenceId);
        }

        alert("Conference resent to all reporters successfully! Rejected reporters' status has been reset to pending.");
      }
    } catch (error) {
      console.error("Error resending to reporters:", error);
    } finally {
      setModifyLoading(false);
    }
  };

  // Handle resend to rejected reporters only
  const handleResendToRejectedReporters = (conference) => {
    const conferenceReporters = allReporters[conference.conferenceId];
    if (conferenceReporters && conferenceReporters.grouped.rejected.length > 0) {
      setSelectedConference(conference);
      setShowResendModal(true);
      // Pre-select all rejected reporters
      setSelectedReportersForResend(conferenceReporters.grouped.rejected.map(r => r.reporterId));
    }
  };

  // Handle resend to selected rejected reporters
  const handleResendToSelectedReporters = async () => {
    if (selectedReportersForResend.length === 0) {
      alert("Please select at least one reporter to resend");
      return;
    }

    setResendLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${selectedConference.conferenceId}/action`,
        {
          action: "modified",
          note: `Resending to ${selectedReportersForResend.length} selected rejected reporters`,
          // Use specific reporter targeting for resend
          allStates: false,
          selectedStates: [],
          adminSelectCities: [],
          adminSelectPincode: "",
          reporterId: selectedReportersForResend
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setShowResendModal(false);
        setSelectedReportersForResend([]);

        // Refresh the reporter data for this specific conference (force refresh)
        await fetchConferenceReporters(selectedConference.conferenceId, true);

        // Also refresh the main conferences list
        await fetchApprovedConferences();

        alert(`Conference resent to ${selectedReportersForResend.length} reporters successfully! Their status has been reset to pending.`);
      }
    } catch (error) {
      console.error("Error resending to selected reporters:", error);
    } finally {
      setResendLoading(false);
    }
  };

  // Handle delete reporter from conference - completely remove from targeting and responses
  const handleDeleteReporter = async (conferenceId, reporterId, reporterName, reporterStatus) => {
    const statusText = reporterStatus === "pending" ? "pending response" : reporterStatus;
    const confirmMessage = `Are you sure you want to completely remove ${reporterName} from this conference?\n\nThis will:\n• Remove them from the targeted reporter list\n• Delete their ${statusText}\n• They will no longer see this conference in their panel\n• This action cannot be undone`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [reporterId]: true }));

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/delete/paid-conference/reporter/${conferenceId}/${reporterId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          timeout: 30000, // 30 second timeout
        }
      );

      if (response.data.success) {
        // Refresh the reporter list for this conference (force refresh)
        await fetchConferenceReporters(conferenceId, true);

        const { removedFromAccepted, removedFromRejected, removedFromTargeted } = response.data.data;
        let message = `${reporterName} has been completely removed from the conference.`;

        if (removedFromTargeted) {
          message += " They will no longer see this conference in their panel.";
        }

        alert(message);
      }
    } catch (error) {
      console.error("Error deleting reporter from conference:", error);

      // Handle different types of errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        alert("Request timed out. Please check your connection and try again.");
      } else if (error.response && error.response.data && error.response.data.message) {
        alert(`Failed to remove reporter: ${error.response.data.message}`);
      } else if (error.response && error.response.status === 404) {
        alert("Reporter not found in this conference.");
      } else if (error.response && error.response.status === 500) {
        alert("Server error. Please try again later.");
      } else {
        alert("Failed to remove reporter from conference. Please check your connection and try again.");
      }
    } finally {
      setDeleteLoading(prev => ({ ...prev, [reporterId]: false }));
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <>
      {/* Compact Search/Filter Bar */}
      <div className="w-full sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-md border-b mb-4 py-3 px-4">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-3 items-center">
            {/* Compact Date Range Filter */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2">
              <FaCalendarAlt className="text-blue-600 text-sm" />
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setTodayOnly(false);
                }}
                max={endDate || undefined}
                placeholder="Start date"
              />
              <span className="text-gray-400 text-sm">→</span>
              <input
                type="date"
                className="border-0 bg-transparent text-sm focus:ring-0 focus:outline-none w-32"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setTodayOnly(false);
                }}
                min={startDate || undefined}
                placeholder="End date"
              />
              <button
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate(today);
                  setTodayOnly(true);
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${todayOnly
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Today
              </button>
            </div>

            {/* Compact Search Input */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-blue-200 px-3 py-2 flex-1 max-w-md">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by Conference ID, State, or City..."
                className="flex-1 border-0 bg-transparent text-sm focus:ring-0 focus:outline-none"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Quick Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSearchText("");
                  setStartDate("");
                  setEndDate("");
                  setTodayOnly(false);
                }}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          All Paid Conferences
        </h2>
        <div className="flex flex-wrap gap-2 sm:space-x-2 sm:space-y-0">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/pending")}
          >
            Pending
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/modified")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/rejected")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/running")}
          >
            Running
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference?status=completed")}
          >
            Completed
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Conference ID</th>
              <th className="px-4 py-2 text-left">Date & Time</th>
              <th className="px-4 py-2 text-left">Location</th>
              <th className="px-4 py-2 text-left">Targeting</th>
              <th className="px-4 py-2 text-left">Reporter Status</th>
              <th className="px-4 py-2 text-left">Proof Status</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredConferences.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No conferences found.
                </td>
              </tr>
            ) : (
              (() => {
                const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
                const paginatedConferences = filteredConferences.slice(
                  (currentPage - 1) * rowsPerPage,
                  currentPage * rowsPerPage
                );
                return paginatedConferences.map((conference) => (
                  <tr key={conference._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-sm">{conference.conferenceId}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="mr-1 text-blue-500" />
                        {conference.conferenceDate}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <FaClock className="mr-1" />
                        {conference.conferenceTime} {conference.timePeriod}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaMapMarkerAlt className="mr-1 text-green-500" />
                        {conference.city}, {conference.state}
                      </div>
                      <div className="text-xs text-gray-500">{conference.place}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        {conference.adminTargeting ? (
                          <>
                            {conference.adminTargeting.allStates ? (
                              <><FaGlobe className="mr-1 text-purple-500" />All States</>
                            ) : conference.adminTargeting.adminSelectState?.length > 0 ? (
                              <><FaBullseye className="mr-1 text-purple-500" />{conference.adminTargeting.adminSelectState.length} States</>
                            ) : conference.adminTargeting.adminSelectCities?.length > 0 ? (
                              <><FaBullseye className="mr-1 text-purple-500" />{conference.adminTargeting.adminSelectCities.length} Cities</>
                            ) : conference.adminTargeting.reporterId?.length > 0 ? (
                              <><FaUsers className="mr-1 text-purple-500" />{conference.adminTargeting.reporterId.length} Reporters</>
                            ) : (
                              <><FaMapMarkerAlt className="mr-1 text-gray-500" />Default</>
                            )}
                          </>
                        ) : (
                          <><FaMapMarkerAlt className="mr-1 text-gray-500" />Default</>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {conference.status === "modified" ? "Modified targeting" : "Original location"}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaUsers className="mr-1 text-blue-500" />
                        {(() => {
                          // Use the detailed reporter data if available, otherwise fallback to conference totals
                          const reporterData = allReporters[conference.conferenceId];
                          if (reporterData) {
                            return `${reporterData.accepted || 0}/${reporterData.totalTargetedReporters || 0} accepted`;
                          }
                          return `${conference.totalAcceptedReporters || 0}/${conference.totalReporters || 0} accepted`;
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(() => {
                          const reporterData = allReporters[conference.conferenceId];
                          if (reporterData) {
                            return `${reporterData.rejected || 0} rejected, ${reporterData.pending || 0} pending`;
                          }
                          return `${conference.totalRejectedReporters || 0} rejected, ${conference.totalPendingReporters || 0} pending`;
                        })()}
                      </div>
                      <div className="text-xs text-gray-400">
                        {(() => {
                          const reporterData = allReporters[conference.conferenceId];
                          if (reporterData) {
                            const accepted = reporterData.accepted || 0;
                            const total = reporterData.totalTargetedReporters || 0;
                            const percentage = total > 0 ? ((accepted / total) * 100).toFixed(1) : 0;
                            return `${percentage}% completion`;
                          }
                          const accepted = conference.totalAcceptedReporters || 0;
                          const total = conference.totalReporters || 0;
                          const percentage = total > 0 ? ((accepted / total) * 100).toFixed(1) : 0;
                          return `${percentage}% completion`;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center text-sm">
                        <FaCheckCircle className="mr-1 text-green-500" />
                        {(() => {
                          const reporterData = allReporters[conference.conferenceId];
                          if (reporterData) {
                            return `${reporterData.completed || 0} submitted`;
                          }
                          return `${conference.totalProofSubmitted || 0} submitted`;
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(() => {
                          const reporterData = allReporters[conference.conferenceId];
                          if (reporterData) {
                            const accepted = reporterData.accepted || 0;
                            const completed = reporterData.completed || 0;
                            return `${accepted - completed} pending`;
                          }
                          return `${conference.totalProofPending || 0} pending`;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${conference.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : conference.status === "modified"
                            ? "bg-orange-100 text-orange-800"
                            : conference.status === "running"
                              ? "bg-blue-100 text-blue-800"
                              : conference.status === "completed"
                                ? "bg-purple-100 text-purple-800"
                                : conference.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : conference.status === "rejected"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                        }`}>
                        {conference.status === "approved" ? "Approved" :
                          conference.status === "modified" ? "Modified" :
                            conference.status === "running" ? "Running" :
                              conference.status === "completed" ? "Completed" :
                                conference.status === "pending" ? "Pending" :
                                  conference.status === "rejected" ? "Rejected" :
                                    conference.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-1">
                        <button
                          className="px-2 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-xs"
                          onClick={() => handleViewDetails(conference)}
                        >
                          <FaEye className="mr-1" />
                          View
                        </button>
                        <button
                          className="px-2 py-1 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center text-xs"
                          onClick={() => handleModifyConference(conference)}
                        >
                          <FaEdit className="mr-1" />
                          Modify
                        </button>
                        {allReporters[conference.conferenceId]?.rejected > 0 && (
                          <button
                            className="px-2 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-xs"
                            onClick={() => handleResendToRejectedReporters(conference)}
                          >
                            <FaSync className="mr-1" />
                            Resend to Reporters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
              })()
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {(() => {
        const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
        return totalPages > 1 ? (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>
              <span className="px-4 py-1 rounded border border-green-400 bg-green-300 text-green-900 font-bold">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-1 rounded border border-green-400 text-green-800 bg-white hover:bg-green-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Next
              </button>
            </nav>
          </div>
        ) : null;
      })()}

      {/* Conference Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-green-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaMicrophone className="mr-2 text-blue-500" />
                {selectedConference.topic}
              </h2>
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${selectedConference.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : selectedConference.status === "modified"
                    ? "bg-orange-100 text-orange-800"
                    : selectedConference.status === "running"
                      ? "bg-blue-100 text-blue-800"
                      : selectedConference.status === "completed"
                        ? "bg-purple-100 text-purple-800"
                        : selectedConference.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedConference.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                }`}>
                {selectedConference.status === "approved" ? "Approved" :
                  selectedConference.status === "modified" ? "Modified" :
                    selectedConference.status === "running" ? "Running" :
                      selectedConference.status === "completed" ? "Completed" :
                        selectedConference.status === "pending" ? "Pending" :
                          selectedConference.status === "rejected" ? "Rejected" :
                            selectedConference.status}
              </span>
            </div>

            {/* Conference Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Conference Information</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Conference ID:</strong> <span className="font-mono">{selectedConference.conferenceId}</span></div>
                  <div><strong>Date:</strong> {selectedConference.conferenceDate}</div>
                  <div><strong>Time:</strong> {selectedConference.conferenceTime} {selectedConference.timePeriod}</div>
                  <div><strong>Purpose:</strong> {selectedConference.purpose}</div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">Location Details</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>State:</strong> {selectedConference.state}</div>
                  <div><strong>City:</strong> {selectedConference.city}</div>
                  <div><strong>Venue:</strong> {selectedConference.place}</div>
                  <div><strong>Landmark:</strong> {selectedConference.landmark || "N/A"}</div>
                </div>
              </div>
            </div>

            {/* All Reporters Who Have Conference in Their Panel */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">All Reporters Who Have This Conference in Their Panel</h3>
              <p className="text-sm text-gray-600 mb-4">
                These are all the reporters who received notifications and have this conference available in their panel to accept or reject.
              </p>

              {allReporters[selectedConference.conferenceId] ? (
                <>

                  {/* Reporter Statistics - Compact with Highlights */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                    <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-gray-700">{allReporters[selectedConference.conferenceId].totalTargetedReporters}</div>
                      <div className="text-xs text-gray-600 font-medium">Total in Panel</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-yellow-700">{allReporters[selectedConference.conferenceId].pending}</div>
                      <div className="text-xs text-yellow-600 font-medium">Pending</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-green-700">{allReporters[selectedConference.conferenceId].accepted}</div>
                      <div className="text-xs text-green-600 font-medium">Accepted</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-red-700">{allReporters[selectedConference.conferenceId].rejected}</div>
                      <div className="text-xs text-red-600 font-medium">Rejected</div>
                    </div>
                    <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-xl font-bold text-blue-700">{allReporters[selectedConference.conferenceId].completed || 0}</div>
                      <div className="text-xs text-blue-600 font-medium">Completed</div>
                    </div>
                  </div>

                  {/* All Reporters Table - Compact with Highlights */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-sm border border-gray-200">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">IINSAF ID</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Notes</th>
                          <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allReporters[selectedConference.conferenceId].reporters.map((reporter, index) => (
                          <tr key={index} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${reporter.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                              reporter.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                                reporter.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                                  "bg-white"
                            }`}>
                            <td className="px-2 py-2 text-xs font-mono font-semibold text-gray-800">{reporter.iinsafId || "N/A"}</td>
                            <td className="px-2 py-2 text-xs font-semibold text-gray-800">{reporter.reporterName || "N/A"}</td>
                            <td className="px-2 py-2 text-xs text-gray-600 truncate max-w-32">{reporter.reporterEmail || "N/A"}</td>
                            <td className="px-2 py-2 text-xs text-gray-600">{reporter.reporterCity}, {reporter.reporterState}</td>
                            <td className="px-2 py-2 text-xs">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${reporter.status === "accepted"
                                  ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                                  : reporter.status === "rejected"
                                    ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                    : reporter.status === "completed"
                                      ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                      : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                                }`}>
                                {reporter.status === "accepted" ? "Accepted" :
                                  reporter.status === "rejected" ? "Rejected" :
                                    reporter.status === "completed" ? "Completed" : "Pending"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-600">
                              {reporter.status === "completed" && reporter.completedAt ?
                                new Date(reporter.completedAt).toLocaleDateString('en-IN') :
                                reporter.acceptedAt ? new Date(reporter.acceptedAt).toLocaleDateString('en-IN') :
                                  reporter.rejectedAt ? new Date(reporter.rejectedAt).toLocaleDateString('en-IN') :
                                    "No response"}
                            </td>
                            <td className="px-2 py-2 text-xs max-w-xs">
                              {reporter.status === "completed" ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">Proof approved</span>
                              ) : reporter.rejectNote ? (
                                <div className="space-y-1">
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-semibold">Rejected</span>
                                  <div className="text-red-600 font-medium break-words">
                                    <strong>Reason:</strong> {reporter.rejectNote}
                                  </div>
                                </div>
                              ) : reporter.proofSubmitted ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">Proof submitted</span>
                              ) : (
                                <span className="text-gray-400">No notes</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              <button
                                onClick={() => handleDeleteReporter(selectedConference.conferenceId, reporter.reporterId, reporter.reporterName, reporter.status)}
                                disabled={deleteLoading[reporter.reporterId]}
                                className={`px-2 py-1 rounded-md text-xs font-bold transition-all duration-200 flex items-center shadow-sm ${deleteLoading[reporter.reporterId]
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200 hover:shadow-md'
                                  }`}
                                title="Completely remove reporter from conference - they will no longer see this conference in their panel"
                              >
                                {deleteLoading[reporter.reporterId] ? (
                                  <>
                                    <FaSpinner className="animate-spin mr-1" />
                                    Removing...
                                  </>
                                ) : (
                                  <>
                                    <FaTrash className="mr-1" />
                                    Delete
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    <FaUsers className="mx-auto text-4xl mb-2" />
                    <p>No reporter data available</p>
                    <p className="text-sm">This conference may not have any targeted reporters yet.</p>
                  </div>
                  <button
                    onClick={() => fetchConferenceReporters(selectedConference.conferenceId, true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center mx-auto"
                  >
                    <FaSync className="mr-2" />
                    Refresh Reporter Data
                  </button>
                </div>
              )}
            </div>

            {/* Admin Targeting Information */}
            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">Admin Targeting Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {selectedConference.adminTargeting ? (
                  <>
                    <div><strong>All States:</strong> {selectedConference.adminTargeting.allStates ? "Yes" : "No"}</div>
                    <div><strong>Selected States:</strong> {selectedConference.adminTargeting.adminSelectState?.join(", ") || "None"}</div>
                    <div><strong>Selected Cities:</strong> {selectedConference.adminTargeting.adminSelectCities?.join(", ") || "None"}</div>
                    <div><strong>Selected Pincode:</strong> {selectedConference.adminTargeting.adminSelectPincode || "None"}</div>
                    <div><strong>Conference Reporters:</strong> {selectedConference.numberOfReporters || 0} need to complete the conference</div>
                    <div><strong>Total sended yet Reporters:</strong> {allReporters[selectedConference.conferenceId]?.totalTargetedReporters || 0} reporters</div>
                    <div><strong>Targeting Method:</strong> {selectedConference.adminTargeting.reporterId?.length > 0 ? "Specific Reporter Selection" :
                      selectedConference.adminTargeting.allStates ? "All States" :
                        selectedConference.adminTargeting.adminSelectState?.length > 0 ? "Selected States/Cities" :
                          "Location-based (State/City)"}</div>
                    <div><strong>Modified At:</strong> {(() => {
                      console.log("🔍 Debug - selectedConference.adminTargeting:", selectedConference.adminTargeting);
                      console.log("🔍 Debug - modifiedAt value:", selectedConference.adminTargeting.modifiedAt);
                      console.log("🔍 Debug - modifiedAt type:", typeof selectedConference.adminTargeting.modifiedAt);
                      return selectedConference.adminTargeting.modifiedAt ? new Date(selectedConference.adminTargeting.modifiedAt).toLocaleString('en-IN') : "N/A";
                    })()}</div>
                  </>
                ) : (
                  <>
                    <div><strong>All States:</strong> No</div>
                    <div><strong>Selected States:</strong> {selectedConference.state || "None"}</div>
                    <div><strong>Selected Cities:</strong> {selectedConference.city || "None"}</div>
                    <div><strong>Selected Pincode:</strong> None</div>
                    <div><strong>Requested Reporters:</strong> {selectedConference.numberOfReporters || 0} reporters</div>
                    <div><strong>Total Targeted Reporters:</strong> {allReporters[selectedConference.conferenceId]?.totalTargetedReporters || 0} reporters</div>
                    <div><strong>Targeting Method:</strong> {selectedConference.reporterId?.length > 0 ? "Specific Reporter Selection" :
                      selectedConference.allStates ? "All States" :
                        selectedConference.adminSelectState?.length > 0 ? "Selected States/Cities" :
                          "Location-based (State/City)"}</div>
                    <div><strong>Modified At:</strong> {selectedConference.modifiedAt ? new Date(selectedConference.modifiedAt).toLocaleString('en-IN') : "N/A"}</div>
                  </>
                )}
              </div>
            </div>


            {/* Admin Actions */}
            <div className="bg-yellow-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
                  onClick={() => handleModifyConference(selectedConference)}
                >
                  <FaEdit className="mr-2" />
                  Modify Targeting
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                  onClick={() => setShowModal(false)}
                >
                  <FaEye className="mr-2" />
                  Close Details
                </button>
              </div>
            </div>

            {/* Submitter Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Submitted By</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Name:</strong> {selectedConference.submittedBy?.name || "N/A"}</div>
                <div><strong>Organization:</strong> {selectedConference.submittedBy?.organization || "N/A"}</div>
                <div><strong>Email:</strong> {selectedConference.submittedBy?.email || "N/A"}</div>
                <div><strong>Press ID:</strong> {selectedConference.submittedBy?.pressConferenceId || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend to Reporters Modal */}
      {showResendModal && selectedConference && allReporters[selectedConference.conferenceId] && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 text-left relative animate-fadeIn border border-blue-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setShowResendModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <FaSync className="mr-2 text-blue-500" />
                Resend Conference to Reporters
              </h2>
              <p className="text-gray-600">Select which reporters to resend the conference to</p>
            </div>

            {/* Conference Info */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Conference: {selectedConference.topic}</h3>
              <p className="text-sm text-blue-700">Conference ID: {selectedConference.conferenceId}</p>
            </div>

            {/* Reporter Status Summary - Compact with Highlights */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 p-2 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-lg font-bold text-gray-700">{allReporters[selectedConference.conferenceId].totalTargetedReporters}</div>
                <div className="text-xs text-gray-600 font-medium">Total</div>
              </div>
              <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 p-2 rounded-lg border border-yellow-200 shadow-sm">
                <div className="text-lg font-bold text-yellow-700">{allReporters[selectedConference.conferenceId].pending}</div>
                <div className="text-xs text-yellow-600 font-medium">Pending</div>
              </div>
              <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-lg border border-green-200 shadow-sm">
                <div className="text-lg font-bold text-green-700">{allReporters[selectedConference.conferenceId].accepted}</div>
                <div className="text-xs text-green-600 font-medium">Accepted</div>
              </div>
              <div className="text-center bg-gradient-to-br from-red-50 to-red-100 p-2 rounded-lg border border-red-200 shadow-sm">
                <div className="text-lg font-bold text-red-700">{allReporters[selectedConference.conferenceId].rejected}</div>
                <div className="text-xs text-red-600 font-medium">Rejected</div>
              </div>
              <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-lg border border-blue-200 shadow-sm">
                <div className="text-lg font-bold text-blue-700">{allReporters[selectedConference.conferenceId].completed || 0}</div>
                <div className="text-xs text-blue-600 font-medium">Completed</div>
              </div>
            </div>

            {/* All Reporters List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                All Reporters Who Have Conference in Panel ({allReporters[selectedConference.conferenceId].totalTargetedReporters})
              </h3>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {allReporters[selectedConference.conferenceId].reporters.map((reporter, index) => (
                  <div key={index} className={`flex items-center p-2 border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ${reporter.status === "completed" ? "bg-gradient-to-r from-green-50 to-emerald-50" :
                      reporter.status === "accepted" ? "bg-gradient-to-r from-blue-50 to-cyan-50" :
                        reporter.status === "rejected" ? "bg-gradient-to-r from-red-50 to-pink-50" :
                          "bg-white"
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedReportersForResend.includes(reporter.reporterId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReportersForResend(prev => [...prev, reporter.reporterId]);
                        } else {
                          setSelectedReportersForResend(prev => prev.filter(id => id !== reporter.reporterId));
                        }
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{reporter.reporterName}</div>
                          <div className="text-xs text-gray-500 truncate max-w-48">{reporter.reporterEmail}</div>
                          <div className="text-xs text-gray-500">{reporter.reporterCity}, {reporter.reporterState}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono text-gray-600 font-semibold">{reporter.iinsafId}</div>
                          <div className="text-xs mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${reporter.status === "accepted"
                                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200"
                                : reporter.status === "rejected"
                                  ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200"
                                  : reporter.status === "completed"
                                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-200"
                                    : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-200"
                              }`}>
                              {reporter.status === "accepted" ? "Accepted" :
                                reporter.status === "rejected" ? "Rejected" :
                                  reporter.status === "completed" ? "Completed" : "Pending"}
                            </span>
                          </div>
                          {reporter.status === "rejected" && reporter.rejectedAt && (
                            <div className="text-xs text-red-600 mt-1">
                              {new Date(reporter.rejectedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {reporter.status === "accepted" && reporter.acceptedAt && (
                            <div className="text-xs text-green-600 mt-1">
                              {new Date(reporter.acceptedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                          {reporter.status === "completed" && reporter.completedAt && (
                            <div className="text-xs text-blue-600 mt-1">
                              {new Date(reporter.completedAt).toLocaleDateString('en-IN')}
                            </div>
                          )}
                        </div>
                      </div>
                      {reporter.rejectNote && (
                        <div className="mt-1 p-1 bg-red-100 rounded text-xs text-red-800">
                          <strong>Reason:</strong> {reporter.rejectNote}
                        </div>
                      )}
                      <div className="ml-2">
                        <button
                          onClick={() => handleDeleteReporter(selectedConference.conferenceId, reporter.reporterId, reporter.reporterName, reporter.status)}
                          disabled={deleteLoading[reporter.reporterId]}
                          className={`px-2 py-1 rounded-md text-xs font-bold transition-all duration-200 flex items-center shadow-sm ${deleteLoading[reporter.reporterId]
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200 hover:shadow-md'
                            }`}
                          title="Completely remove reporter from conference"
                        >
                          {deleteLoading[reporter.reporterId] ? (
                            <>
                              <FaSpinner className="animate-spin mr-1" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <FaTrash className="mr-1" />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selection Summary */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-800">Selected Reporters</h4>
                  <p className="text-sm text-blue-600">
                    {selectedReportersForResend.length} of {allReporters[selectedConference.conferenceId].totalTargetedReporters} reporters selected
                  </p>
                  <div className="text-xs text-gray-600 mt-1">
                    Selected: {selectedReportersForResend.length} |
                    Pending: {allReporters[selectedConference.conferenceId].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "pending").length} |
                    Accepted: {allReporters[selectedConference.conferenceId].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "accepted").length} |
                    Rejected: {allReporters[selectedConference.conferenceId].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "rejected").length} |
                    Completed: {allReporters[selectedConference.conferenceId].reporters.filter(r => selectedReportersForResend.includes(r.reporterId) && r.status === "completed").length}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedReportersForResend(
                      allReporters[selectedConference.conferenceId].reporters.map(r => r.reporterId)
                    )}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend(
                      allReporters[selectedConference.conferenceId].grouped.rejected.map(r => r.reporterId)
                    )}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    Select Rejected Only
                  </button>
                  <button
                    onClick={() => setSelectedReportersForResend([])}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResendModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleResendToSelectedReporters}
                disabled={selectedReportersForResend.length === 0 || resendLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {resendLoading ? (
                  <>
                    <FaSpinner className="mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <FaSync className="mr-2" />
                    Resend to {selectedReportersForResend.length} Reporters
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovedPaidConference;
